import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { useMemo, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-lodash-debounce';

import useAxiosHook from '../../../../../../hooks/useAxiosHook';
import { WithData } from '../../../../../../types/api';
import AutoCompleteInput from '../../../../../Forms/AutoCompleteInput/AutoCompleteInput';
import FieldWithErrors from '../../../../../Forms/ReactHookForm/FieldWithErrors';
import type {
  FormValues,
  ImportListTypesData,
} from '../CreateEditDialog.functions';

function OrdersTab() {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<FormValues>();

  const [importedListTypeFilter, setImportedListTypeFilter] = useState('');
  const debouncedImportedListTypeFilter = useDebounce(importedListTypeFilter);

  const importTypeLists = useWatch<FormValues, 'import_list_types'>({
    control,
    name: 'import_list_types',
  });

  const { data: importedListTypes } = useAxiosHook<
    WithData<ImportListTypesData[]>
  >('/importlisttypes?name=' + debouncedImportedListTypeFilter);

  const importedListTypeOptions = useMemo(() => {
    if (!importedListTypes?.data) return [];

    const diff = importedListTypes.data.filter(
      (lt) => !importTypeLists?.some((f) => f.id === lt.id)
    );

    return diff.map((lt) => ({
      value: { id: lt.id, name: lt.name },
      label: lt.name,
    }));
  }, [importedListTypes, importTypeLists]);

  const handleAdd = (e: any) => {
    if (!e) return;
    const { id, name } = e;

    const current = importTypeLists || [];
    setValue('import_list_types', [...current, { id, name }]);
    setImportedListTypeFilter('');
  };

  const handleRemove = (item: { id: number; name: string }) => {
    const current = importTypeLists || [];

    const updated = current.filter((f) => f.id !== item.id);
    setValue('import_list_types', updated);
  };

  return (
    <div className="orders-tab">
      <span className="dialog-section-divider">{t('Order settings')}</span>

      <div className="orders-flags">
        <FieldWithErrors
          name="allow_incomplete_address"
          label={false}
          className=""
        >
          <Controller
            name="allow_incomplete_address"
            render={({ field }) => (
              <>
                <Checkbox
                  id="allow_incomplete_address"
                  name="allow_incomplete_address"
                  value={field.value}
                  checked={field.value}
                  onChange={field.onChange}
                  data-cy="allow_incomplete_address"
                />
                <label
                  htmlFor="allow_incomplete_address"
                  data-cy="allow_incomplete_address"
                >
                  {t('Allow incomplete address')}
                </label>
              </>
            )}
          />
        </FieldWithErrors>

        <FieldWithErrors name="orders_on_map_access" label={false} className="">
          <Controller
            name="orders_on_map_access"
            render={({ field }) => (
              <>
                <Checkbox
                  id="orders_on_map_access"
                  name="orders_on_map_access"
                  value={field.value}
                  checked={field.value}
                  onChange={field.onChange}
                  data-cy="orders_on_map_access"
                />
                <label
                  htmlFor="orders_on_map_access"
                  data-cy="orders_on_map_access"
                >
                  {t('Access orders on map')}
                </label>
              </>
            )}
          />
        </FieldWithErrors>

        <FieldWithErrors name="proof_of_delivery" label={false} className="">
          <Controller
            name="proof_of_delivery"
            render={({ field }) => (
              <>
                <Checkbox
                  id="proof_of_delivery"
                  name="proof_of_delivery"
                  value={field.value}
                  checked={field.value}
                  onChange={field.onChange}
                  data-cy="proof_of_delivery"
                />
                <label htmlFor="proof_of_delivery" data-cy="proof_of_delivery">
                  {t('Confirmation receipt')}
                </label>
              </>
            )}
          />
        </FieldWithErrors>
      </div>

      <span className="dialog-section-divider">{t('Assigned list types')}</span>

      <>
        <AutoCompleteInput
          id="client_list_type_add"
          filterValue={importedListTypeFilter}
          value={null}
          options={importedListTypeOptions}
          onFilterChange={setImportedListTypeFilter}
          onSelectionChange={handleAdd}
          filterDataCy="list_types"
          optionsClassName="data-cy-list_types-options"
        />

        <div className="assigned-templates-container">
          {importTypeLists.map((typeList) => (
            <div key={typeList.id} className="assigned-template">
              <Button
                type="button"
                icon="fas fa-times"
                className="p-button-plain p-button-text"
                onClick={() => handleRemove(typeList)}
              />
              <div className="assigned-template-name">{typeList.name}</div>
            </div>
          ))}
        </div>
      </>
    </div>
  );
}

export default OrdersTab;
